import React, { ReactNode, useCallback, useContext, useMemo } from 'react'
import Avatar, {
  IProps as IAvatarProps,
  ugcUserLink,
} from 'Components/author/avatar'
import classNames from 'classnames'
import useToggleFollow from 'Components/hooks/use-toggle-follow'
import {
  preventDefault,
  stopPropagation,
} from 'Components/new/community-card/interact'
import { GlobalStoreContext } from 'Components/store'
import ToolTip from 'Components/new/tool-tip'
import FontSize10 from 'Components/font-size-10'
import { size14Class } from 'Components/new/community-card/text'
import { IMedalProps, Medal } from 'Components/new/community-card/user-detail'
import { Image } from 'Components/view-image'

export const AuthLabel: React.FC = () => {
  return (
    <FontSize10
      width={28}
      height={16}
      className="tw-ml-4 tw-bg-common-orange tw-bg-opacity-10 tw-text-common-orange tw-rounded-2 tw-align-text-bottom"
    >
      作者
    </FontSize10>
  )
}

export interface IProps {
  userId?: IAvatarProps['userId']
  avatar?: IAvatarProps['avatar']
  userVerified?: IAvatarProps['userVerified']
  verifyType?: IAvatarProps['verifyType']
  username?: string
  nameAppend?: ReactNode
  nameClassName?: string
  textTags?: string[] // 文字标签
  medals?: IMedalProps[] // 奖章
  medalsVisible?: boolean // 暂时控制个人页不显示徽章详情
  levelIcon?: string
  followed?: boolean
  followVisible?: boolean
  userDetail?: ReactNode
  className?: string
  onClickFollow?(curFollow: boolean): void
  onClickUser?(): void
}

const Header: React.FC<IProps> = ({
  avatar,
  userId,
  username,
  nameAppend,
  nameClassName,
  userVerified,
  verifyType,
  textTags,
  medals,
  medalsVisible = true,
  levelIcon,
  followed,
  followVisible = true,
  userDetail,
  className,
  onClickFollow,
  onClickUser,
}) => {
  const { update: toggleFollow, curStatus: curFollow } = useToggleFollow({
    userId,
    followed,
  })
  const ugcUserLinkWrapper = ugcUserLink({ userId, username })

  const handleFollow = stopPropagation(
    preventDefault(
      useCallback(() => {
        if (onClickFollow) {
          onClickFollow(curFollow)
        }
        toggleFollow()
      }, [onClickFollow, toggleFollow, curFollow])
    )
  )

  const { state } = useContext(GlobalStoreContext)

  const curUserId = state.userData?.user_id

  const handleClickUser = stopPropagation(onClickUser)

  return userId ? (
    <React.Fragment>
      <div className={classNames('tw-flex tw-items-center', className)}>
        <div className="tw-flex-1">
          {ugcUserLinkWrapper(
            <div className="tw-overflow-hidden tw-flex tw-items-center tw-pr-16">
              <ToolTip
                theme={'light'}
                content={userDetail}
                className="tw-flex-none"
                tag={'div'}
              >
                <Avatar
                  avatar={avatar}
                  userVerified={userVerified}
                  verifyType={verifyType}
                  username={username}
                  onClick={handleClickUser}
                  hoverTransition
                />
              </ToolTip>
              <div className="tw-ml-16 tw-flex tw-flex-col tw-justify-center">
                <div className="tw-overflow-hidden tw-flex tw-items-center tw-h-20">
                  <p
                    className={classNames(
                      ' line-1 g-active-link-text',
                      nameClassName,
                      {
                        'tw-font-medium': !nameClassName,
                      }
                    )}
                    onClick={handleClickUser}
                  >
                    <span className="tw-text-16 tw-text-black">{username}</span>
                    <span className="tw-text-14 tw-text-color-gray-900">
                      {nameAppend}
                    </span>
                  </p>
                  {medals?.map((item, index) => {
                    return (
                      <React.Fragment key={item.icon}>
                        <ToolTip
                          className={classNames(
                            'tw-flex-none tw-relative tw-h-18 tw-flex tw-items-center tw-bg-opacity-50',
                            index === 0 ? 'tw-ml-8' : 'tw-ml-4'
                          )}
                          tag="div"
                          theme="light"
                          placement="top"
                          content={medalsVisible ? <Medal {...item} /> : null}
                        >
                          <Image
                            className="tw-h-20"
                            width={20}
                            height={20}
                            key={item.icon}
                            src={item.icon || ''}
                            alt={item.name}
                          />
                        </ToolTip>
                      </React.Fragment>
                    )
                  })}
                  {levelIcon ? (
                    <Image
                      className="tw-h-20 tw-ml-8"
                      src={levelIcon}
                      width={20}
                      height={20}
                    />
                  ) : null}
                </div>
                {textTags?.length ? (
                  <div className="tw-h-16 tw-mt-4 tw-flex tw-items-center">
                    {textTags.map((item, index) => {
                      return (
                        <span
                          className={classNames(
                            'tw-text-12 tw-text-video-shallow-gray',
                            index === 0 ? '' : 'tw-ml-4'
                          )}
                          key={item}
                        >
                          {item}
                        </span>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
        {!followVisible ||
        String(curUserId) === userId ||
        curUserId === userId ? null : (
          <button
            type={'button'}
            className={classNames(
              'tw-font-medium tw-h-20 tw-leading-20',
              curFollow ? 'tw-text-common-green' : 'tw-text-common-orange',
              size14Class
            )}
            onClick={handleFollow}
          >
            {curFollow ? null : (
              <i className="DCD_Icon icon_add_24 tw-text-14" />
            )}
            {curFollow ? '已关注' : '关注'}
          </button>
        )}
      </div>
    </React.Fragment>
  ) : null
}

export default Header
